import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { useStoreBase } from "@/lib/store";
import { Button } from "@/components/ui/button";

function inr(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN")}`;
}

export default function StorefrontBuildBox() {
  const { boxSlug } = useParams();
  const { slug, base } = useStoreBase();
  const nav = useNavigate();
  const [box, setBox] = useState(null);
  const [products, setProducts] = useState([]);
  const [picked, setPicked] = useState([]);
  const [filter, setFilter] = useState("All");
  const [err, setErr] = useState("");

  useEffect(() => {
    setErr("");
    api.get(`/store/${slug}/collections/${boxSlug}`)
      .then(({ data }) => {
        setBox(data.box);
        setProducts(data.products || []);
        setPicked([]);
      })
      .catch((e) => setErr(extractErr(e) || "Box not found"));
  }, [slug, boxSlug]);

  const count = Number(box?.item_count || 0);
  const cats = useMemo(() => {
    const names = [];
    products.forEach((p) => {
      if (p.category_name && !names.includes(p.category_name)) names.push(p.category_name);
    });
    return names;
  }, [products]);
  const visible = filter === "All" ? products : products.filter((p) => p.category_name === filter);
  const qtyOf = (id) => picked.filter((x) => x === id).length;
  const full = count > 0 && picked.length >= count;
  const original = picked.reduce((sum, id) => sum + Number(products.find((p) => p.id === id)?.price || 0), 0);

  const add = (p) => {
    if (full) return;
    const stock = p.stock;
    const next = qtyOf(p.id) + 1;
    if (stock != null && stock !== "" && Number(stock) < next) return;
    setPicked((cur) => [...cur, p.id]);
  };
  const remove = (id) => {
    setPicked((cur) => {
      const i = cur.lastIndexOf(id);
      if (i < 0) return cur;
      return cur.filter((_, idx) => idx !== i);
    });
  };
  const pay = () => {
    if (!full || !box) return;
    nav(`${base}/checkout`, { state: { buildBox: { box_id: box.id, product_ids: picked, name: box.name, price: box.price } } });
  };

  if (err) {
    return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-stone-500" data-testid="build-box-missing">{err}</div>;
  }
  if (!box) {
    return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-stone-500">Loading box…</div>;
  }

  const desktop = box.banner_desktop || box.banner_mobile;
  const mobile = box.banner_mobile || box.banner_desktop;

  return (
    <div className="pb-28" data-testid="build-box-page">
      {desktop || mobile ? (
        <div data-testid="build-box-banner">
          {desktop ? <img src={mediaUrl(desktop)} alt="" className="hidden md:block w-full max-h-[420px] object-cover" /> : null}
          {mobile ? <img src={mediaUrl(mobile)} alt="" className="md:hidden w-full max-h-[280px] object-cover" /> : null}
        </div>
      ) : (
        <div className="bg-sky-100 px-6 py-12 text-center">
          <h1 className="text-4xl font-black text-stone-900">{box.headline}</h1>
          <p className="mt-2 text-2xl font-black">{box.offer_text} <span className="text-yellow-400" style={{ WebkitTextStroke: "2px #111" }}>{inr(box.price)}</span></p>
        </div>
      )}

      <div className="bg-white border-b border-stone-100" data-testid="build-box-steps">
        <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-8">
          <div className="inline-flex items-center h-9 px-4 rounded-sm bg-[#2f6bff] text-white text-sm font-bold">Build Your Box In 2 Steps</div>
          <div className="flex items-center gap-2 text-sm text-stone-700">
            <span className="w-6 h-6 rounded-full bg-sky-100 text-sky-700 font-bold inline-flex items-center justify-center">1</span>
            Choose &amp; Add {count} Items To Your Box
          </div>
          <div className="flex items-center gap-2 text-sm text-stone-700">
            <span className="w-6 h-6 rounded-full bg-sky-100 text-sky-700 font-bold inline-flex items-center justify-center">2</span>
            Open Your Cart &amp; Click Pay Now To Avail Offer
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <h2 className="text-center text-3xl font-black text-stone-900">Our Products</h2>
        {cats.length ? (
          <div className="mt-4 flex flex-wrap justify-center gap-2" data-testid="build-box-filters">
            {["All", ...cats].map((c) => (
              <button key={c} type="button" onClick={() => setFilter(c)} className={`h-9 px-4 rounded-full text-sm border ${filter === c ? "bg-sky-100 border-sky-200 text-sky-800" : "bg-white border-stone-200 text-stone-600"}`}>{c}</button>
            ))}
          </div>
        ) : null}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {visible.map((p) => {
            const n = qtyOf(p.id);
            return (
              <article key={p.id} className="rounded-2xl border border-stone-200 bg-white overflow-hidden flex flex-col" data-testid={`build-box-product-${p.id}`}>
                <div className="aspect-square bg-stone-50">
                  {p.image ? <img src={mediaUrl(p.image)} alt="" className="w-full h-full object-contain" /> : null}
                </div>
                <div className="p-3 flex flex-col flex-1">
                  <div className="font-bold text-stone-900 leading-snug line-clamp-2">{p.name}</div>
                  <div className="mt-1 text-sm text-stone-700">{inr(p.price)}</div>
                  <div className="mt-auto pt-3">
                    {n > 0 ? (
                      <div className="flex items-center justify-between gap-2">
                        <button type="button" className="h-9 w-9 rounded-full border" onClick={() => remove(p.id)} aria-label="Remove one">−</button>
                        <span className="text-sm font-bold" data-testid={`build-box-qty-${p.id}`}>{n} in box</span>
                        <button type="button" className="h-9 w-9 rounded-full border" onClick={() => add(p)} disabled={full} aria-label="Add one">+</button>
                      </div>
                    ) : (
                      <Button type="button" className="w-full rounded-lg bg-[#2f6bff] hover:bg-[#2458d6]" disabled={full} onClick={() => add(p)} data-testid={`build-box-add-${p.id}`}>Add To Box</Button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[80] w-[min(560px,calc(100%-1.5rem))]" data-testid="build-box-float">
        <div className="rounded-full bg-stone-900 text-white shadow-xl px-4 py-3 flex items-center gap-3">
          <div className="min-w-0">
            <div className="text-sm font-bold" data-testid="build-box-counter">{picked.length} / {count} added</div>
            {full ? (
              <div className="text-xs text-white/80">
                <span className="line-through mr-2" data-testid="build-box-original">{inr(original)}</span>
                <span className="font-bold text-white" data-testid="build-box-final">{inr(box.price)}</span>
              </div>
            ) : (
              <div className="text-xs text-white/70">Add {count - picked.length} more</div>
            )}
          </div>
          <button type="button" disabled={!full} onClick={pay} className="ml-auto h-10 px-5 rounded-full bg-[#2f6bff] disabled:bg-white/20 font-bold text-sm" data-testid="build-box-pay">
            Pay now
          </button>
        </div>
      </div>
    </div>
  );
}
